import {
  activityCodeDescriptionMap,
  EntityType,
  type EnrichedUser,
  type EnrichedDevice,
  type EnrichedNotificationRule,
} from '@eppendorf/vnls-audit-event-client';
import {
  Button,
  DotsLoader,
  Icon,
  IconSizeClasses,
} from '@eppendorf/vnls-react-components';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { getIconNameForActivityCode } from '$features/audit-events/audit-events.utils';
import { AuditEventDescription } from '$features/audit-events/components/description/description';

import { useLatestAuditTrail } from '../dashboard.api';

import { DashboardCardTitle } from './dashboard-card-title';
import { DashboardWidget } from './dashboard-widget';
import { SectionFeedback } from './section-feedback';
import { TruncatedText } from './truncated-text';

import './latest-audit-trail-card.scss';

export function LatestAuditTrailCard() {
  const { t } = useTranslation();
  const { data, isLoading, isError, refetch } = useLatestAuditTrail();

  return (
    <DashboardWidget className="latest-audit-trail-card">
      <div className="dashboard-widget__header">
        <DashboardCardTitle
          icon="audit"
          title={t('dashboard.latestAuditTrail.title')}
          hint={t('dashboard.latestAuditTrail.hint')}
        />
        <SectionFeedback sectionId="latest-audit-trail" />
      </div>
      {isLoading && <DotsLoader className="m-top-l" />}
      {isError && (
        <div className="dashboard-card__feedback" role="alert">
          <span>{t('dashboard.latestAuditTrail.loadError')}</span>
          <Button
            variant="secondary"
            onClick={() => {
              refetch();
            }}
          >
            {t('dashboard.latestAuditTrail.retry')}
          </Button>
        </div>
      )}
      {!isLoading && !isError && (!data || data.length === 0) && (
        <div className="latest-audit-trail-card__empty">
          {t('dashboard.latestAuditTrail.noEvents')}
        </div>
      )}
      {!isLoading && !isError && data && data.length > 0 && (
        <div className="latest-audit-trail-card__list">
          {data.map((event) => {
            const { name } = getIconNameForActivityCode(event.activityCode);
            const description =
              event.activityCodeDescription ||
              activityCodeDescriptionMap[event.activityCode] ||
              'Unknown activity';

            const performedBy = (() => {
              const originType = event.origin.type;
              const originEntity = event.metadata?.originEntity;

              if (originType === EntityType.User) {
                const user = originEntity as EnrichedUser | undefined;
                const fullName =
                  `${user?.firstName || ''} ${user?.lastName || ''}`.trim();
                return fullName || user?.email || t('shared.unknown');
              }

              if (originType === EntityType.Device) {
                const device = originEntity as EnrichedDevice | undefined;
                return device?.name || device?.serialNumber || t('shared.unknown');
              }

              if (originType === EntityType.NotificationRule) {
                const rule = originEntity as EnrichedNotificationRule | undefined;
                return rule?.name || t('shared.unknown');
              }

              return t('shared.unknown');
            })();

            return (
              <Link
                key={event.id}
                className="latest-audit-trail-card__item"
                to={{
                  search: `?sidecarRoute=audit-log/detail&id=${event.id}`,
                }}
              >
                <div className="latest-audit-trail-card__icon">
                  <Icon name={name} size={IconSizeClasses.XSmall} />
                </div>
                <TruncatedText
                  text={description}
                  className="latest-audit-trail-card__activity"
                />
                <TruncatedText className="latest-audit-trail-card__details">
                  <AuditEventDescription event={event} />
                </TruncatedText>
                <span className="latest-audit-trail-card__timestamp">
                  {new Date(event.timestamp).toLocaleString('en-GB', {
                    day: '2-digit',
                    month: 'short',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
                <TruncatedText
                  text={performedBy}
                  className="latest-audit-trail-card__performed-by"
                />
              </Link>
            );
          })}
        </div>
      )}
    </DashboardWidget>
  );
}
